import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { apiClient } from '@services/api';
import Icon from '@material-ui/core/Icon';
import Alert from '@material-ui/lab/Alert';
import Modal from '@components/Modal';
import Button from '@components/Button';
import Table from '@components/Table';
import useIsNarrow from '@utils/useIsNarrow';

// Dupla custódia cruzada: só esses 2 e-mails liberam/recusam (mesma regra do backend,
// RefundRequest::allowedEmails()). A tela subiu de "Administrar" pra "Geral" (2026-08-13)
// pra todo consultor ativo poder ver os próprios pedidos + comprovante, mas ação
// (Liberar/Recusar) continua exclusiva desses 2 — escondida aqui, travada de novo no
// backend (approve/refuse já validam o e-mail, isso é só UI).
const APPROVER_EMAILS = ['marcelo@fulltour.com.br', 'logistica@fulltour.com.br'];

const STATUS = {
  pending:  { label: 'Aguardando', bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  approved: { label: 'Estornado',  bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
  refused:  { label: 'Recusado',   bg: 'rgba(239,68,68,0.15)',  color: '#f87171' },
  // Dupla custódia interna já cumprida (marcelo@/logistica@ liberaram), mas a Asaas ainda
  // não confirmou de verdade — comum em PIX, que às vezes exige autorização manual no
  // PAINEL da Asaas antes de processar (status "AWAITING_CRITICAL_ACTION_AUTHORIZATION").
  // Enquanto isso o dinheiro não voltou pro cliente, então NÃO pode aparecer como
  // "Estornado" (verde) — fica nesse estado até o comprovante chegar (2026-08-13).
  awaiting_confirmation: { label: 'Aguardando confirmação', bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
};

const brl = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Um estorno pode ter mais de 1 comprovante: se o valor pedido é maior que uma parcela, o
// Asaas completa tirando de outra(s) parcela(s), cada uma com seu próprio comprovante.
// row.receipt_urls é a fonte nova (array de OBJETOS {value, transactionReceiptUrl, status}
// — mesmo formato salvo em refund_requests.receipt_urls no backend); fallback pro
// row.receipt_url antigo (string única) pra registros aprovados antes desse fix. Sempre
// devolve um array de STRINGS prontas pra usar em href — sem isso o href virava
// "[object Object]" (bug 2026-08-11). (2026-08-11)
const getReceiptUrls = (row) => {
  const raw = (Array.isArray(row.receipt_urls) && row.receipt_urls.length)
    ? row.receipt_urls
    : (row.receipt_url ? [row.receipt_url] : []);
  return raw
    .map((r) => (typeof r === 'string' ? r : r?.transactionReceiptUrl))
    .filter(Boolean);
};

// Status exibido != status gravado quando aprovado mas sem comprovante ainda (ver
// STATUS.awaiting_confirmation acima). Backend continua com status='approved' — isso é
// só apresentação, pra não mostrar "Estornado" antes da Asaas confirmar de verdade.
const getDisplayStatus = (row) => {
  if (row.status === 'approved' && getReceiptUrls(row).length === 0) {
    return STATUS.awaiting_confirmation;
  }
  return STATUS[row.status] || STATUS.pending;
};

const Index = (props) => {
  const narrow = useIsNarrow();
  const myEmail = (props.user?.data?.email || '').toLowerCase().trim();
  const isApprover = APPROVER_EMAILS.includes(myEmail);
  const [filter, setFilter] = useState('pending');
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  const [decision, setDecision] = useState(null); // { row, action }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [receiptsRow, setReceiptsRow] = useState(null); // row cujos comprovantes estão abertos no modal
  const [reasonRow, setReasonRow] = useState(null); // row cujo motivo completo está aberto no modal (coluna corta com ellipsis)

  useEffect(() => { if (props.setShowMenu) props.setShowMenu(true); }, []);

  const doRefresh = () => { setRefresh(true); setTimeout(() => setRefresh(false), 300); };

  const confirmDecision = () => {
    if (!decision) return;
    setBusy(true); setError(null);
    apiClient.httpInstance.post(`/refunds/${decision.row.id}/${decision.action}`, { note: note.trim() || undefined })
      .then(() => {
        setOk(decision.action === 'approve' ? 'Estorno liberado e enviado ao Asaas.' : 'Pedido recusado.');
        setDecision(null); setNote(''); doRefresh();
        setTimeout(() => setOk(null), 4000);
      })
      .catch((e) => setError(e?.response?.data?.message || 'Não foi possível concluir a ação.'))
      .finally(() => setBusy(false));
  };

  const columns = [
    {
      headerName: 'Cliente', field: 'customer_name', flex: 1, headerClassName: 'super-app-theme--header',
      renderCell: (p) => <span>{p.row.customer_name || '—'}</span>,
    },
    {
      headerName: 'Referência', field: 'sale_reference', flex: 1, headerClassName: 'super-app-theme--header',
      renderCell: (p) => (
        <div style={{ lineHeight: '18px' }}>
          <div>{p.row.sale_reference || '—'}</div>
          <small style={{ color: '#8a9ab5', fontSize: 11 }}>{p.row.pay_id}</small>
        </div>
      ),
    },
    {
      headerName: 'Tipo', field: 'type', width: 90, headerClassName: 'super-app-theme--header',
      renderCell: (p) => <span>{p.row.type === 'partial' ? 'Parcial' : 'Integral'}</span>,
    },
    {
      headerName: 'Valor', field: 'value', width: 120, headerClassName: 'super-app-theme--header',
      renderCell: (p) => <span style={{ fontWeight: 600 }}>{brl(p.row.value)}</span>,
    },
    {
      // Ellipsis + title (tooltip nativo do browser) era difícil de ler em motivos longos —
      // clique abre modal com o texto completo (2026-08-16).
      headerName: 'Motivo', field: 'reason', flex: 1, headerClassName: 'super-app-theme--header',
      renderCell: (p) => (
        <span className="cursor-pointer" onClick={(e) => { e.stopPropagation(); setReasonRow(p.row); }}
          title="Clique para ver o motivo completo"
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
          {p.row.reason}
        </span>
      ),
    },
    {
      headerName: 'Status', field: 'status', width: 150, headerClassName: 'super-app-theme--header',
      renderCell: (p) => {
        const st = getDisplayStatus(p.row);
        return <span style={{ background: st.bg, color: st.color, borderRadius: 12, padding: '3px 10px', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>● {st.label}</span>;
      },
    },
    {
      headerName: 'Solicitado por', field: 'requested_by_email', flex: 1, headerClassName: 'super-app-theme--header',
      renderCell: (p) => <small>{p.row.requested_by_email}</small>,
    },
    {
      headerName: 'Liberado por', field: 'approved_by_email', flex: 1, headerClassName: 'super-app-theme--header',
      renderCell: (p) => <small>{p.row.approved_by_email || '—'}</small>,
    },
    {
      headerName: 'Comprovante', field: 'receipt_url', width: 130, sortable: false, preventRedirect: true, headerClassName: 'super-app-theme--header',
      renderCell: (p) => {
        const urls = getReceiptUrls(p.row);
        if (!urls.length) return <span style={{ color: '#8a9ab5' }}>—</span>;
        return (
          <a className="flex items-center cursor-pointer" style={{ textDecoration: 'underline', color: '#62DEC2' }}
             onClick={(e) => { e.stopPropagation(); setReceiptsRow(p.row); }}>
            {urls.length > 1 ? `Visualizar (${urls.length})` : 'Visualizar'} <Icon style={{ fontSize: 14, marginLeft: 4 }}>open_in_new</Icon>
          </a>
        );
      },
    },
    // Coluna Ações só existe pra quem pode liberar/recusar (marcelo@/logistica@) — pra
    // todo mundo mais é leitura + comprovante, sem essa coluna na tabela.
    ...(isApprover ? [{
      headerName: 'Ações', field: 'acoes', width: 150, sortable: false, preventRedirect: true, headerClassName: 'super-app-theme--header',
      renderCell: (p) => {
        if (p.row.status !== 'pending') return <span style={{ color: '#8a9ab5' }}>—</span>;
        return (
          <div className="flex" style={{ gap: 12 }}>
            <a className="cursor-pointer" style={{ color: '#34d399', fontWeight: 600 }}
               onClick={(e) => { e.stopPropagation(); setDecision({ row: p.row, action: 'approve' }); setNote(''); setError(null); }}>Liberar</a>
            <a className="cursor-pointer" style={{ color: '#f87171', fontWeight: 600 }}
               onClick={(e) => { e.stopPropagation(); setDecision({ row: p.row, action: 'refuse' }); setNote(''); setError(null); }}>Recusar</a>
          </div>
        );
      },
    }] : []),
  ];

  return (
    <div className="px-7">
      <div className="flex items-center mt-2 text-2xl text-blue mb-3">
        <Icon className="mr-2">replay</Icon>
        <span>Reembolsos</span>
      </div>

      <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap', width: narrow ? '100%' : undefined }}>
        {[['pending', 'Aguardando'], ['approved', 'Estornados'], ['refused', 'Recusados'], ['', 'Todos']].map(([v, label]) => (
          <button key={v || 'all'} type="button" onClick={() => setFilter(v)}
            style={{
              padding: '7px 16px', borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              flex: narrow ? '1 1 auto' : undefined,
              border: filter === v ? '2px solid #47B098' : '1px solid #1e3a5f',
              background: filter === v ? 'rgba(71,176,152,0.15)' : 'transparent',
              color: filter === v ? '#62DEC2' : '#8a9ab5',
            }}>{label}</button>
        ))}
      </div>

      {ok && <Alert className="mb-3" severity="success">{ok}</Alert>}

      <Table
        path="/refunds"
        onRowClick={null}
        refresh={refresh}
        params={{ status: filter || undefined, pageSize: 30 }}
        columns={columns}
        /* Celular/tablet: card com tudo que precisa pra decidir Liberar/Recusar sem
           voltar pro desktop — motivo inteiro, valor em destaque, ações sempre à mão. (2026-08) */
        renderMobileCard={(row) => {
          const st = getDisplayStatus(row);
          const receiptUrls = getReceiptUrls(row);
          return (
            <div style={{ padding: '14px', borderBottom: '1px solid #1e3a5a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, color: '#e8f0fb', fontWeight: 600 }}>{row.customer_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#8ba5c4', fontFamily: 'monospace', marginTop: 2 }}>
                    {row.sale_reference || '—'}{row.pay_id ? ' · ' + row.pay_id : ''}
                  </div>
                </div>
                <span style={{ background: st.bg, color: st.color, borderRadius: 12, padding: '3px 10px', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>● {st.label}</span>
              </div>

              <div style={{ display: 'flex', gap: 18, margin: '10px 0 8px' }}>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Tipo</div>
                  <div style={{ fontSize: 14, color: '#e8f0fb' }}>{row.type === 'partial' ? 'Parcial' : 'Integral'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Valor</div>
                  <div style={{ fontSize: 16, color: '#f5c842', fontWeight: 700 }}>{brl(row.value)}</div>
                </div>
              </div>

              {row.reason && (
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#c3d3e6', lineHeight: 1.5, marginBottom: 8 }}>
                  <span style={{ color: '#64748b', fontWeight: 700 }}>Motivo: </span>{row.reason}
                </div>
              )}

              <div style={{ fontSize: 11, color: '#64748b', marginBottom: row.status === 'pending' || receiptUrls.length ? 10 : 0 }}>
                Solicitado por {row.requested_by_email || '—'}
                {row.approved_by_email && <> · liberado por {row.approved_by_email}</>}
              </div>

              {receiptUrls.length > 0 && (
                <a className="cursor-pointer"
                   onClick={(e) => { e.stopPropagation(); setReceiptsRow(row); }}
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#62DEC2', fontSize: 12, textDecoration: 'underline', marginBottom: row.status === 'pending' ? 10 : 0 }}>
                  {receiptUrls.length > 1 ? `Ver comprovantes (${receiptUrls.length})` : 'Ver comprovante'} <Icon style={{ fontSize: 14 }}>open_in_new</Icon>
                </a>
              )}

              {row.status === 'pending' && isApprover && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setDecision({ row, action: 'approve' }); setNote(''); setError(null); }}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: 'rgba(52,211,153,0.15)', color: '#34d399', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    Liberar
                  </button>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setDecision({ row, action: 'refuse' }); setNote(''); setError(null); }}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: 'rgba(248,113,113,0.15)', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    Recusar
                  </button>
                </div>
              )}
            </div>
          );
        }}
      />

      <Modal open={!!decision} close={() => !busy && setDecision(null)} style={{ width: '100%', maxWidth: '460px' }}>
        {decision && (
          <>
            <div className="flex justify-between">
              <span className="text-xl text-blue">{decision.action === 'approve' ? 'Liberar estorno' : 'Recusar pedido'}</span>
              <Icon className="cursor-pointer" onClick={() => !busy && setDecision(null)}>close</Icon>
            </div>
            <small className="text-gray block mt-1">
              {decision.row.customer_name || 'Cliente não informado'}
              {decision.row.sale_reference ? <> &nbsp;·&nbsp; OR <b>{decision.row.sale_reference}</b></> : null}
              {decision.row.pay_id ? <> &nbsp;·&nbsp; {decision.row.pay_id}</> : null}
            </small>
            <div className="mt-3" style={{
              background: decision.action === 'approve' ? '#ecfdf5' : '#fef2f2',
              border: '1px solid ' + (decision.action === 'approve' ? '#a7f3d0' : '#fecaca'),
              borderRadius: 8, padding: 14,
            }}>
              <p className="text-blue" style={{ fontWeight: 600 }}>
                {decision.action === 'approve'
                  ? <>Tem certeza que deseja <b>liberar</b> o estorno <b>{decision.row.type === 'partial' ? 'Parcial' : 'Integral'}</b> no valor de <b>{brl(decision.row.value)}</b>{decision.row.customer_name ? <> para <b>{decision.row.customer_name}</b></> : null}?</>
                  : <>Tem certeza que deseja <b>recusar</b> o pedido de estorno de <b>{brl(decision.row.value)}</b>?</>}
              </p>
              {decision.action === 'approve' &&
                <small className="text-gray">Isso envia o estorno ao Asaas agora e não pode ser desfeito por aqui.</small>}
            </div>
            {decision.row.reason && (
              <div className="mt-2" style={{ background: 'rgba(71,176,152,0.08)', border: '1px solid #d7e6e2', borderRadius: 8, padding: '10px 12px' }}>
                <span className="text-blue" style={{ fontWeight: 700, fontSize: 12, display: 'block', marginBottom: 2 }}>Motivo do pedido</span>
                <span className="text-gray" style={{ fontSize: 13, lineHeight: 1.5 }}>{decision.row.reason}</span>
              </div>
            )}
            <label className="text-sm text-blue mt-3 block">Observação {decision.action === 'refuse' ? '(motivo da recusa)' : '(opcional)'}</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full mt-1 p-2 border rounded" />
            {error && <Alert className="mt-2" severity="error">{error}</Alert>}
            <div className="justify-end mt-4 md:flex w-100">
              <Button textcolor="#172B4D" variant="outlined" padding="10px 15px" margintop="0px" marginright="20px"
                width="120px" onClick={() => setDecision(null)} bgColor="transparent" border="1px solid #172B4D" disabled={busy}>Cancelar</Button>
              <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="150px"
                onClick={confirmDecision} disabled={busy}
                customcolor={decision.action === 'refuse' ? '#b91c1c' : undefined}>
                {busy ? 'Processando...' : (decision.action === 'approve' ? 'Sim, liberar' : 'Sim, recusar')}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!receiptsRow} close={() => setReceiptsRow(null)} style={{ width: '100%', maxWidth: '420px' }}>
        {receiptsRow && (
          <>
            <div className="flex justify-between">
              <span className="text-xl text-blue">Comprovantes do estorno</span>
              <Icon className="cursor-pointer" onClick={() => setReceiptsRow(null)}>close</Icon>
            </div>
            <small className="text-gray block mt-1">
              {receiptsRow.customer_name || 'Cliente não informado'}
              {receiptsRow.sale_reference ? <> &nbsp;·&nbsp; OR <b>{receiptsRow.sale_reference}</b></> : null}
            </small>
            {/* Mais de 1 comprovante acontece quando o valor pedido é maior que uma parcela —
                o Asaas completa tirando de outra(s) parcela(s), cada uma com seu próprio link.
                Botão outlined, mesmo padrão do "Cancelar" nos modais de decisão desta tela —
                em vez de link solto (2026-08-11). window.open explícito, não href, pra nunca
                arriscar navegação interna acidental se a url vier mal formada. */}
            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {getReceiptUrls(receiptsRow).map((url, i) => (
                <Button key={i} type="button" textcolor="#172B4D" variant="outlined" padding="10px 15px"
                  margintop="0px" bgColor="transparent" border="1px solid #172B4D"
                  onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                  Comprovante {i + 1}
                </Button>
              ))}
            </div>
            <div className="justify-end mt-4 md:flex w-100">
              <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="120px"
                onClick={() => setReceiptsRow(null)}>Fechar</Button>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!reasonRow} close={() => setReasonRow(null)} style={{ width: '100%', maxWidth: '420px' }}>
        {reasonRow && (
          <>
            <div className="flex justify-between">
              <span className="text-xl text-blue">Motivo do pedido</span>
              <Icon className="cursor-pointer" onClick={() => setReasonRow(null)}>close</Icon>
            </div>
            <small className="text-gray block mt-1">
              {reasonRow.customer_name || 'Cliente não informado'}
              {reasonRow.sale_reference ? <> &nbsp;·&nbsp; OR <b>{reasonRow.sale_reference}</b></> : null}
            </small>
            <div className="mt-3" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '12px 14px' }}>
              <span className="text-blue" style={{ fontSize: 14, lineHeight: 1.6 }}>{reasonRow.reason || '—'}</span>
            </div>
            <div className="justify-end mt-4 md:flex w-100">
              <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="120px"
                onClick={() => setReasonRow(null)}>Fechar</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(Index);
